import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints, type DiscoveryKeyword } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/Card";
import { fmtTimestamp } from "@/lib/format";
import { SectionTabs, DISCOVERY_TABS } from "@/components/SectionTabs";

// Keywords — v2.2.4 in-app editor for `discovery_keywords` table.
// keyword_discovery cron at 04:00 reads from the same table, so adding
// a keyword here takes effect on tomorrow's tick. Last-run signal
// (results found / new channels seeded / error) is shown per keyword
// so the operator can prune ineffective queries.
export default function Keywords() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const list = useQuery({
    queryKey: ["discovery-keywords"],
    queryFn: () => endpoints.listDiscoveryKeywords(),
    refetchInterval: 60_000,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["discovery-keywords"] });

  const createMut = useMutation({
    mutationFn: (keyword: string) => endpoints.createDiscoveryKeyword(keyword),
    onSuccess: () => {
      setDraft("");
      invalidate();
    },
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      endpoints.toggleDiscoveryKeyword(id, enabled),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => endpoints.deleteDiscoveryKeyword(id),
    onSuccess: invalidate,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    createMut.mutate(trimmed);
  };

  const rows = list.data ?? [];
  const enabledCount = rows.filter((r) => r.enabled).length;
  const totalNewChannelsRecent = rows.reduce(
    (s, r) => s + (r.last_run_new_channels ?? 0),
    0,
  );

  return (
    <div className="h-full overflow-auto">
      <SectionTabs tabs={DISCOVERY_TABS} />
      <div className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-xl font-semibold">发现关键词</h1>
        <span className="text-xs text-muted-foreground">
          {enabledCount} 启用 / {rows.length} 总数 · 上次跑共新增{" "}
          {totalNewChannelsRecent} 个候选频道
        </span>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-sm">新增关键词</CardTitle>
          <CardDescription>
            每个关键词会在每个平台 (YouTube + X) 各跑一次搜索;
            修改后等明早 04:00 cron 触发,或在系统状态页手动 run-job keyword_discovery
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="例如:Bybit 邀请码 / OKX referral code / KuCoin promo"
              maxLength={200}
              className="flex-1 rounded border border-border bg-background px-3 py-1.5 text-sm"
              disabled={createMut.isPending}
            />
            <Button type="submit" size="sm" disabled={createMut.isPending || !draft.trim()}>
              {createMut.isPending ? "添加中…" : "添加"}
            </Button>
          </form>
          {createMut.isError && (
            <p className="mt-2 text-xs text-destructive">
              添加失败:{(createMut.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">关键词列表</CardTitle>
        </CardHeader>
        <CardContent>
          {list.isLoading && (
            <p className="text-sm text-muted-foreground">加载中…</p>
          )}
          {/* v2.3.4 — surface fetch errors explicitly. Without this, a 5xx or
              network failure looks identical to the "table genuinely empty"
              state, which confused troubleshooting when keyword_discovery
              cron looked silently dead. */}
          {list.isError && (
            <p className="text-sm text-destructive">
              加载失败:{(list.error as Error).message}
            </p>
          )}
          {!list.isLoading && !list.isError && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              还没有任何关键词。在上面添加一个,或检查 secrets seed 是否已运行。
            </p>
          )}
          {rows.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-2">关键词</th>
                  <th className="py-2 pr-2">状态</th>
                  <th className="py-2 pr-2">上次运行</th>
                  <th className="py-2 pr-2">搜到结果</th>
                  <th className="py-2 pr-2">新增频道</th>
                  <th className="py-2 pr-2">错误</th>
                  <th className="py-2 pr-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((kw) => (
                  <KeywordRow
                    key={kw.id}
                    kw={kw}
                    onToggle={(enabled) =>
                      toggleMut.mutate({ id: kw.id, enabled })
                    }
                    onDelete={() => {
                      if (confirm(`删除关键词 "${kw.keyword}"?`)) {
                        deleteMut.mutate(kw.id);
                      }
                    }}
                    busy={toggleMut.isPending || deleteMut.isPending}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

function KeywordRow({
  kw,
  onToggle,
  onDelete,
  busy,
}: {
  kw: DiscoveryKeyword;
  onToggle: (enabled: boolean) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 pr-2 font-medium">{kw.keyword}</td>
      <td className="py-2 pr-2">
        {kw.enabled ? (
          <Badge variant="success">启用</Badge>
        ) : (
          <Badge variant="secondary">已停用</Badge>
        )}
      </td>
      <td className="py-2 pr-2 text-muted-foreground">
        {kw.last_run_at ? fmtTimestamp(kw.last_run_at) : "从未"}
      </td>
      <td className="py-2 pr-2 tabular-nums">
        {kw.last_run_result_count ?? "—"}
      </td>
      <td className="py-2 pr-2 tabular-nums">
        {kw.last_run_new_channels !== undefined ? (
          <span
            className={
              kw.last_run_new_channels === 0
                ? "text-muted-foreground"
                : "font-medium"
            }
          >
            {kw.last_run_new_channels}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="py-2 pr-2 text-xs">
        {kw.last_run_error ? (
          // v2.4.7 — replace 40-char truncation + hover tooltip (which
          // most operators don't discover) with explicit click-to-expand.
          // Full error often runs hundreds of chars (twscrape rate-limit
          // detail, Apify quota messages) and the operator needs the tail.
          <ErrorCell error={kw.last_run_error} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-2 pr-2 text-right">
        <div className="flex justify-end gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggle(!kw.enabled)}
            disabled={busy}
          >
            {kw.enabled ? "停用" : "启用"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            disabled={busy}
            className="text-destructive hover:text-destructive"
          >
            删除
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ErrorCell renders a per-row error with click-to-expand. v2.4.7 — replaces
// the silent 40-char truncation + tooltip pattern, which broke when the
// error tail (rate-limit code, account id) sat past char 40.
function ErrorCell({ error }: { error: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = error.length > 40;
  if (!isLong || expanded) {
    return (
      <div className="text-destructive">
        <p className="whitespace-pre-wrap break-words">{error}</p>
        {isLong && (
          <button
            onClick={() => setExpanded(false)}
            className="mt-1 text-[10px] text-muted-foreground hover:underline"
          >
            收起
          </button>
        )}
      </div>
    );
  }
  return (
    <button
      onClick={() => setExpanded(true)}
      className="text-left text-destructive hover:underline"
      title="点开看完整错误"
    >
      {error.slice(0, 40)}… <span className="text-muted-foreground">[展开]</span>
    </button>
  );
}
